import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-apollo';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { TOPIC_TYPES, TOPICS } from './constants';
import Button from '../Button';
import Angle from '../Angle';
import OnboardingForm from './components/OnboardingForm';
import OwnerEmailForm from './components/OwnerEmailForm';
import FeedbackForm from './components/FeedbackForm';
import DismissButton from '../DismissButton';
import Select from '../Select';
import { FeedbackCreate as FeedbackCreateMutation } from '../../product/SupportModal/graphql/mutations';
import { getAsset } from '../../../util/assets';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { getErrorString } from '../../../util/normalize-errors';
import { MeQuery } from '../../../graphql/queries/account';
import { isValidEmail } from '../../../util/email';

const propTypes = {
  onClose: PropTypes.func,
  onRequestClose: PropTypes.func,
  dismissible: PropTypes.bool,
};

const ConsumerFeedback = ({ onClose, onRequestClose, dismissible = true }) => {
  const [topic, setTopic] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();
  const openAlert = useOpenAlert();
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const [onFeedbackCreate] = useMutation(FeedbackCreateMutation);

  const onSubmit = async values => {
    if (submitting) {
      return;
    }

    if (!isValidEmail(values.email_address)) {
      openAlert({ type: 'error', message: 'Invalid email address' });
      return;
    }

    setSubmitting(true);
    try {
      await onFeedbackCreate({
        variables: {
          input: {
            platform: 'WEB',
            url: location.pathname,
            email_address: values.email_address,
            message: `
            title: ${values.title}

            description: ${values.description ?? 'N/A'}

            hierarchy_item_id: ${values.hierarchy_item_id ?? 'N/A'}
            `,
          },
        },
      });

      setSuccess(true);
    } catch (e) {
      openAlert({
        type: 'error',
        message: getErrorString(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Fragment>
        {dismissible && (
          <div className="text-right">
            <DismissButton onClick={onClose ?? onRequestClose} />
          </div>
        )}
        <div className="text-center mb-2">
          <img
            src={getAsset('/waldo-excited.png')}
            width="40px"
            alt="Waldo Exited"
          />
        </div>
        <p className="text-center h3">Thank you for your feedback!</p>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {dismissible && (
        <div className="text-right">
          <DismissButton onClick={onClose ?? onRequestClose} />
        </div>
      )}
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          title: '',
          description: '',
          email_address: user?.email_address ?? '',
          hierarchy_item_id: '',
        }}
        render={({ submitForm, resetForm, values, handleChange }) => (
          <Fragment>
            <h4 className="text-center">
              {topic ? 'Details' : 'Have feedback?'}
            </h4>
            {!topic && (
              <div className="topics-container">
                {TOPICS.map(t => (
                  <Button
                    key={t.key}
                    className="topic"
                    onClick={() => {
                      handleChange({
                        target: {
                          name: 'title',
                          value: t.label,
                        },
                      });
                      handleChange({
                        target: {
                          name: 'description',
                          value: '',
                        },
                      });
                      handleChange({
                        target: {
                          name: 'hierarchy_item_id',
                          value: '',
                        },
                      });
                      setTopic(t);
                    }}
                  >
                    <p>{t.label}</p> <Angle direction="right" />
                  </Button>
                ))}
              </div>
            )}
            {topic && (
              <Fragment>
                <Button
                  btnStyle="link"
                  className="mb-1"
                  onClick={() => {
                    resetForm();
                    setTopic(null);
                  }}
                >
                  <Angle direction="left" className="mr-1" /> Back
                </Button>
                <Select
                  id="title"
                  name="title"
                  label="What issue are you facing?"
                  value={topic?.key}
                  onChange={({ target }) => {
                    const selectedTopic = TOPICS.find(
                      t => t.key === target.value,
                    );

                    handleChange({
                      target: {
                        name: 'title',
                        value: selectedTopic.label,
                      },
                    });
                    handleChange({
                      target: {
                        name: 'description',
                        value: '',
                      },
                    });
                    handleChange({
                      target: {
                        name: 'hierarchy_item_id',
                        value: '',
                      },
                    });
                    setTopic(selectedTopic);
                  }}
                >
                  {TOPICS.map(t => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </Fragment>
            )}
            {topic?.type === TOPIC_TYPES.ONBOARDING && (
              <OnboardingForm values={values} handleChange={handleChange} />
            )}
            {topic?.type === TOPIC_TYPES.OWNER_EMAIL && (
              <OwnerEmailForm values={values} handleChange={handleChange} />
            )}
            {[TOPIC_TYPES.FEEDBACK, TOPIC_TYPES.OTHER].includes(
              topic?.type,
            ) && <FeedbackForm values={values} handleChange={handleChange} />}
            {[
              TOPIC_TYPES.ONBOARDING,
              TOPIC_TYPES.FEEDBACK,
              TOPIC_TYPES.OTHER,
            ].includes(topic?.type) && (
              <div className="text-center">
                <Button
                  type="submit"
                  onClick={submitForm}
                  disabled={(() => {
                    if (submitting) {
                      return true;
                    }

                    switch (topic?.type) {
                      case TOPIC_TYPES.ONBOARDING: {
                        return (
                          !values.title ||
                          !values.hierarchy_item_id ||
                          !values.email_address
                        );
                      }
                      case TOPIC_TYPES.OWNER_EMAIL: {
                        return !values.title || !values.hierarchy_item_id;
                      }
                      case TOPIC_TYPES.FEEDBACK:
                      case TOPIC_TYPES.OTHER: {
                        return (
                          !values.title ||
                          !values.description ||
                          !values.email_address
                        );
                      }
                      default: {
                        return !values.title;
                      }
                    }
                  })()}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            )}
          </Fragment>
        )}
      />
    </Fragment>
  );
};

ConsumerFeedback.propTypes = propTypes;

export default ConsumerFeedback;
